/**
 * Carer-guidance HTML renderer — emits a foldable section structure
 * for one level's parsed markdown.
 *
 * The librarian view (this surface) defaults to **all sections
 * expanded**, in contrast to iOS which defaults to all collapsed.
 * Reason: the librarian's job is *authoring* the whole content; the
 * carer's is *consuming* one section at a time. The fold-state Set
 * tracks which sections the librarian has explicitly *collapsed* —
 * empty Set ⇒ everything open.
 *
 * Inline body markdown (bold, italic, lists, links) is rendered via
 * `marked` with raw HTML disabled (XSS surface stays closed). The
 * structural H1/H2 parsing belongs to `guidanceParser.ts`, not to
 * `marked`.
 *
 * Filed under OMON-068. Pairs with iOS's `GuidanceSheet.swift`.
 */

import { marked } from "marked";

import type { ParsedGuidanceLevel } from "./guidanceParser";
import { escapeHtml } from "../utils/html";

// Configure marked: GFM-flavoured, line breaks honoured.
//
// Trust model: the catalogue markdown is *trusted source* — Firestore
// security rules restrict writes to authenticated librarians, so raw
// HTML embedded in the markdown is rendered as-is. We do not sanitise.
// If the trust model ever widens (e.g. carers gain write access to
// guidance content), wrap output in DOMPurify or switch to a tokenizer
// that strips raw HTML before that change ships.
//
// `async: false` keeps `marked.parse()` synchronous (string return, not
// Promise<string>) so the renderer can compose its HTML inline.
marked.use({
  gfm: true,
  breaks: true,
  async: false,
});

/**
 * Render one parsed level as fold-aware HTML.
 *
 * @param parsed       parser output for this level
 * @param level        1-based level index, used in the fold-state key
 * @param collapsedSet keys of sections the librarian has manually
 *                     collapsed (empty = all expanded)
 */
export function render(
  parsed: ParsedGuidanceLevel,
  level: number,
  collapsedSet: Set<string>,
): string {
  const parts: string[] = [];

  if (parsed.preamble) {
    parts.push(
      `<div class="guidance-preamble">${escapeHtml(parsed.preamble)}</div>`,
    );
  }

  for (const section of parsed.sections) {
    const foldKey = `level-${level}::${section.key}`;
    const isCollapsed = collapsedSet.has(foldKey);
    const openAttr = isCollapsed ? "" : " open";
    const bodyHtml = section.body ? marked.parse(section.body) as string : "";

    parts.push(`
      <details class="guidance-section"${openAttr} data-fold-key="${escapeHtml(foldKey)}">
        <summary class="guidance-summary">
          <span class="chevron">▸</span>
          <span class="guidance-heading">${escapeHtml(section.heading)}</span>
        </summary>
        <div class="guidance-body">${bodyHtml}</div>
      </details>
    `);
  }

  if (parts.length === 0) {
    return "";
  }

  return `<div class="guidance-rendered">${parts.join("")}</div>`;
}
